<template>
  <NavBar />
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4">Reguli Dunning</h1>
          <div>
            <v-btn color="info" variant="outlined" class="mr-2" @click="runDryRun" :loading="loadingDryRun">
              <v-icon start>mdi-play-circle-outline</v-icon>
              Test Dry Run
            </v-btn>
            <v-btn color="primary" @click="openCreateDialog">
              <v-icon start>mdi-plus</v-icon>
              Regula Noua
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card variant="outlined" class="mb-2">
          <v-card-text class="py-2">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <ClientSearchFilter
                  v-model="filters.client_id"
                  :show-include-subclients="false"
                  client-select-label="Filtreaza dupa Client"
                  density="compact"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="filters.service_id"
                  label="Filtreaza dupa Serviciu"
                  :items="filterServicesForSelect"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="filters.service_type_id"
                  label="Filtreaza dupa Tip Serviciu"
                  :items="filterServiceTypesForSelect"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-switch
                  v-model="filters.include_subclients"
                  label="Inclusiv subclienti"
                  color="primary"
                  hide-details
                  density="compact"
                  class="mr-2"
                />
                <v-btn icon="mdi-magnify" color="primary" variant="tonal" size="small" @click="loadRules" :loading="loading" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Rules Table -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Reguli Configurate
            <v-spacer />
            <v-btn icon="mdi-refresh" variant="text" @click="loadRules" :loading="loading" />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="ruleHeaders"
              :items="rules"
              :loading="loading"
              class="elevation-1"
              density="compact"
            >
              <template #[`item.managed_by_client_name`]="{ item }">
                <span v-if="item.managed_by_client_name">{{ item.managed_by_client_name }}</span>
                <span v-else class="text-grey">Global</span>
              </template>
              <template #[`item.days_offset`]="{ item }">
                <v-chip
                  :color="item.days_offset < 0 ? 'info' : item.days_offset === 0 ? 'warning' : 'error'"
                  size="small"
                >
                  {{ item.days_offset < 0 ? item.days_offset : '+' + item.days_offset }} zile
                </v-chip>
              </template>
              <template #[`item.action_type`]="{ item }">
                <v-chip size="small" :color="getActionColor(item.action_type)">
                  {{ item.action_type_label }}
                </v-chip>
              </template>
              <template #[`item.blocks_client`]="{ item }">
                <v-icon v-if="item.blocks_client" color="error">mdi-lock</v-icon>
                <span v-else>-</span>
              </template>
              <template #[`item.enabled`]="{ item }">
                <v-switch
                  :model-value="item.enabled"
                  color="success"
                  hide-details
                  density="compact"
                  @update:model-value="toggleRule(item)"
                />
              </template>
              <template #[`item.scope`]="{ item }">
                <span v-if="item.service_name">{{ item.service_name }}</span>
                <span v-else-if="item.service_type_name">Tip: {{ item.service_type_name }}</span>
                <span v-else class="text-grey">Global</span>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="openEditDialog(item)" />
                <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(item)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Logs Table -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Jurnal Executie
            <v-spacer />
            <v-btn icon="mdi-refresh" variant="text" @click="loadLogs" :loading="loadingLogs" />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="logHeaders"
              :items="logs"
              :loading="loadingLogs"
              class="elevation-1"
              density="compact"
            >
              <template #[`item.status`]="{ item }">
                <v-chip :color="item.status === 'success' ? 'success' : 'error'" size="small">
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.executed_at`]="{ item }">
                {{ formatDateTime(item.executed_at) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="700">
      <v-card>
        <v-card-title>{{ editingRule ? 'Editeaza Regula' : 'Regula Noua' }}</v-card-title>
        <v-card-text>
          <v-form ref="ruleForm">
            <v-text-field
              v-model="form.name"
              label="Nume regula *"
              :rules="[v => !!v || 'Numele este obligatoriu']"
              class="mb-2"
            />
            <v-textarea
              v-model="form.description"
              label="Descriere"
              rows="2"
              class="mb-2"
            />
            <div class="mb-2">
              <div class="text-subtitle-2 mb-1">Client care gestioneaza regula</div>
              <ClientSearchFilter
                v-model="form.managed_by_client_id"
                :show-include-subclients="false"
                client-select-label="Selecteaza Client"
              />
            </div>
            <v-row>
              <v-col cols="6">
                <v-autocomplete
                  v-model="form.service_id"
                  label="Serviciu (optional)"
                  :items="servicesForSelect"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="form.service_type_id"
                  label="Tip Serviciu (optional)"
                  :items="serviceTypesForSelect"
                  clearable
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="4">
                <v-text-field
                  v-model.number="form.days_offset"
                  label="Zile offset *"
                  type="number"
                  hint="Negativ=inainte, 0=la expirare, Pozitiv=dupa"
                  persistent-hint
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="form.action_type"
                  label="Tip actiune *"
                  :items="actionTypes"
                  :rules="[v => !!v || 'Selecteaza actiunea']"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model.number="form.priority"
                  label="Prioritate"
                  type="number"
                  hint="0 = cea mai mare prioritate"
                  persistent-hint
                />
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="6">
                <v-switch
                  v-model="form.blocks_client"
                  label="Blocheaza clientul"
                  color="error"
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-switch
                  v-model="form.enabled"
                  label="Regula activa"
                  color="success"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-divider class="my-4" />
            <div class="text-subtitle-2 mb-2">Template Notificare (optional)</div>
            <v-text-field
              v-model="form.notification_subject"
              label="Subiect email"
              hint="Variabile: {client_name}, {service_name}, {expiration_date}, {days_remaining}"
              persistent-hint
              class="mb-2"
            />
            <v-textarea
              v-model="form.notification_body"
              label="Corp mesaj"
              rows="4"
              hint="Variabile: {client_name}, {service_name}, {expiration_date}, {days_remaining}, {cs_id}"
              persistent-hint
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">Anuleaza</v-btn>
          <v-btn color="primary" @click="submitRule" :loading="saving">
            {{ editingRule ? 'Salveaza' : 'Creeaza' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dry Run Results Dialog -->
    <v-dialog v-model="dryRunDialog" max-width="800">
      <v-card>
        <v-card-title>Rezultate Dry Run</v-card-title>
        <v-card-text>
          <v-alert v-if="dryRunResults.length === 0" type="info" variant="tonal">
            Nicio actiune de executat.
          </v-alert>
          <v-data-table
            v-else
            :headers="dryRunHeaders"
            :items="dryRunResults"
            density="compact"
          >
            <template #[`item.action_type`]="{ item }">
              <v-chip size="small" :color="getActionColor(item.action_type)">
                {{ item.action_type }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red" @click="dryRunDialog = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirmare Stergere</v-card-title>
        <v-card-text>
          Sigur vrei sa stergi regula "<strong>{{ deletingRule?.name }}</strong>"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Anuleaza</v-btn>
          <v-btn color="error" @click="deleteRule" :loading="deleting">Sterge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { getDunningRules, createDunningRule, updateDunningRule, deleteDunningRule, toggleDunningRule, getDunningLogs, dunningDryRun, getDunningActionTypes, getDunningAccessibleServices } from '@/composables/useDunningAPI'
import NavBar from '@/components/NavBar.vue'
import ClientSearchFilter from '@/components/ClientSearchFilter.vue'

export default {
  name: 'DunningRulesView',
  components: { NavBar, ClientSearchFilter },
  data() {
    return {
      rules: [],
      logs: [],
      actionTypes: [],
      servicesList: [],
      serviceTypesList: [],
      allServicesList: [],
      allServiceTypesList: [],
      filters: {
        client_id: null,
        service_id: null,
        service_type_id: null,
        include_subclients: true
      },
      loading: false,
      loadingLogs: false,
      loadingDryRun: false,
      saving: false,
      deleting: false,
      dialog: false,
      deleteDialog: false,
      dryRunDialog: false,
      editingRule: null,
      deletingRule: null,
      dryRunResults: [],
      form: this.defaultForm(),
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      ruleHeaders: [
        { title: 'Nume', key: 'name' },
        { title: 'Client', key: 'managed_by_client_name' },
        { title: 'Serviciu/Tip', key: 'scope' },
        { title: 'Zile', key: 'days_offset', width: '100px' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Blocheaza', key: 'blocks_client', width: '90px' },
        { title: 'Prioritate', key: 'priority', width: '90px' },
        { title: 'Activ', key: 'enabled', width: '90px' },
        { title: 'Actiuni', key: 'actions', sortable: false, width: '100px' }
      ],

      logHeaders: [
        { title: 'Regula', key: 'rule_name' },
        { title: 'CS ID', key: 'client_service_id' },
        { title: 'Client', key: 'client_name' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'Status', key: 'status' },
        { title: 'Mesaj', key: 'message' },
        { title: 'Data', key: 'executed_at' }
      ],

      dryRunHeaders: [
        { title: 'Regula', key: 'rule_name' },
        { title: 'Actiune', key: 'action_type' },
        { title: 'CS ID', key: 'cs_id' },
        { title: 'Client', key: 'client_name' },
        { title: 'Serviciu', key: 'service_name' },
        { title: 'Expira', key: 'expiration_date' }
      ]
    }
  },
  computed: {
    token() {
      return this.$store.getters.getToken
    },
    servicesForSelect() {
      return this.servicesList.map(s => ({ value: s.id, title: s.name }))
    },
    serviceTypesForSelect() {
      return this.serviceTypesList.map(st => ({ value: st.id, title: st.name }))
    },
    filterServicesForSelect() {
      return this.allServicesList.map(s => ({ value: s.id, title: s.name }))
    },
    filterServiceTypesForSelect() {
      return this.allServiceTypesList.map(st => ({ value: st.id, title: st.name }))
    }
  },
  mounted() {
    this.loadRules()
    this.loadLogs()
    this.loadActionTypes()
    this.loadAccessibleServices(null)
    this.loadFilterServices(null)
  },
  watch: {
    'form.managed_by_client_id'(newClientId) {
      // Reload services when managed_by_client_id changes in the form
      this.form.service_id = null
      this.form.service_type_id = null
      this.loadAccessibleServices(newClientId)
    },
    'filters.client_id'(newClientId) {
      // Reload filter services when filter client changes
      this.filters.service_id = null
      this.filters.service_type_id = null
      this.loadFilterServices(newClientId)
      this.loadRules()
    },
    'filters.include_subclients'() {
      this.loadRules()
    }
  },
  methods: {
    defaultForm() {
      return {
        name: '',
        description: '',
        managed_by_client_id: null,
        service_id: null,
        service_type_id: null,
        days_offset: 0,
        action_type: null,
        blocks_client: false,
        enabled: true,
        priority: 0,
        notification_subject: '',
        notification_body: ''
      }
    },

    async loadRules() {
      this.loading = true
      const filterParams = {}
      if (this.filters.client_id) filterParams.client_id = this.filters.client_id
      if (this.filters.service_id) filterParams.service_id = this.filters.service_id
      if (this.filters.service_type_id) filterParams.service_type_id = this.filters.service_type_id
      if (this.filters.client_id) filterParams.include_subclients = this.filters.include_subclients
      const result = await getDunningRules(this.token, filterParams)
      if (!result.error) {
        this.rules = result.rules
      }
      this.loading = false
    },

    async loadLogs() {
      this.loadingLogs = true
      const result = await getDunningLogs(this.token)
      if (!result.error) {
        this.logs = result.logs
      }
      this.loadingLogs = false
    },

    async loadActionTypes() {
      const result = await getDunningActionTypes(this.token)
      if (!result.error) {
        this.actionTypes = result.action_types
      }
    },

    async loadAccessibleServices(clientId) {
      try {
        const result = await getDunningAccessibleServices(this.token, clientId)
        if (!result.error) {
          this.servicesList = result.services || []
          this.serviceTypesList = result.service_types || []
        }
      } catch (e) {
        console.error('Error loading accessible services:', e)
      }
    },

    async loadFilterServices(clientId) {
      try {
        const result = await getDunningAccessibleServices(this.token, clientId)
        if (!result.error) {
          this.allServicesList = result.services || []
          this.allServiceTypesList = result.service_types || []
        }
      } catch (e) {
        console.error('Error loading filter services:', e)
      }
    },

    openCreateDialog() {
      this.editingRule = null
      this.form = this.defaultForm()
      this.dialog = true
    },

    openEditDialog(rule) {
      this.editingRule = rule
      this.form = {
        name: rule.name,
        description: rule.description || '',
        managed_by_client_id: rule.managed_by_client_id,
        service_id: rule.service_id,
        service_type_id: rule.service_type_id,
        days_offset: rule.days_offset,
        action_type: rule.action_type,
        blocks_client: rule.blocks_client,
        enabled: rule.enabled,
        priority: rule.priority,
        notification_subject: rule.notification_subject || '',
        notification_body: rule.notification_body || ''
      }
      this.dialog = true
    },

    async submitRule() {
      this.saving = true
      let result
      if (this.editingRule) {
        result = await updateDunningRule(this.token, { id: this.editingRule.id, ...this.form })
      } else {
        result = await createDunningRule(this.token, this.form)
      }
      this.saving = false

      if (result.error) {
        this.showSnackbar(result.message, 'error')
      } else {
        this.showSnackbar(this.editingRule ? 'Regula actualizata' : 'Regula creata', 'success')
        this.dialog = false
        this.loadRules()
      }
    },

    async toggleRule(rule) {
      const result = await toggleDunningRule(this.token, rule.id)
      if (!result.error) {
        this.loadRules()
      }
    },

    confirmDelete(rule) {
      this.deletingRule = rule
      this.deleteDialog = true
    },

    async deleteRule() {
      this.deleting = true
      const result = await deleteDunningRule(this.token, this.deletingRule.id)
      this.deleting = false

      if (result.error) {
        this.showSnackbar(result.message, 'error')
      } else {
        this.showSnackbar('Regula stearsa', 'success')
        this.deleteDialog = false
        this.loadRules()
      }
    },

    async runDryRun() {
      this.loadingDryRun = true
      const result = await dunningDryRun(this.token)
      this.loadingDryRun = false

      if (!result.error) {
        this.dryRunResults = result.results || []
        this.dryRunDialog = true
      } else {
        this.showSnackbar(result.message, 'error')
      }
    },

    getActionColor(actionType) {
      const colors = {
        notify_email: 'blue',
        notify_in_app: 'blue-lighten-2',
        notify_both: 'blue-darken-1',
        expire_cs: 'warning',
        suspend_cs: 'orange',
        terminate_cs: 'error',
        suspend_client: 'orange-darken-2',
        block_client: 'red-darken-2',
        suspend_all_services: 'red-darken-1',
        cascade_terminate_msisdn: 'deep-purple',
        deprovision_mno: 'purple',
        downgrade_client_type: 'brown'
      }
      return colors[actionType] || 'grey'
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('ro-RO', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>
